import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { serviceDeliveryAPI, teamAPI } from '../lib/api';
import { useAuthStore } from '../store';
import {
  Plus, Loader2, Edit, Trash2, CheckCircle2, Archive, ListChecks, User, Calendar, RotateCcw, UserPlus,
  ChevronDown, ChevronRight, CheckSquare, CornerDownRight,
} from 'lucide-react';

const AREA_OPTIONS = [
  { value: 'contenido', label: 'Contenido' },
  { value: 'ads', label: 'Ads' },
  { value: 'finanzas', label: 'Finanzas' },
  { value: 'otro', label: 'Otro' },
];

const areaColor = {
  contenido: 'bg-violet-100 text-violet-700',
  ads: 'bg-amber-100 text-amber-700',
  finanzas: 'bg-emerald-100 text-emerald-700',
  otro: 'bg-gray-100 text-gray-700',
};
const areaLabel = (v) => AREA_OPTIONS.find((o) => o.value === v)?.label || v;

const fmtDate = (d) => {
  if (!d) return null;
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const emptyDraft = () => ({ descripcion: '', area: 'otro', responsable_id: 'none', fecha: '' });

export default function WeeklyActionsSection({ clientId, clientName = '' }) {
  const { user } = useAuthStore();
  const isCEO = user?.role === 'ceo';
  const [view, setView] = useState('activas'); // 'activas' | 'archivo'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subActionId, setSubActionId] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [subDraft, setSubDraft] = useState({ descripcion: '', responsable_id: 'none' });
  const [subSaving, setSubSaving] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.listWeeklyActions(clientId, view === 'archivo');
      setItems(res.items || []);
    } catch (e) {
      toast.error(e.message || 'Error al cargar acciones');
    }
    setLoading(false);
  }, [clientId, view]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    teamAPI.getAll().then((t) => setTeam((t || []).filter((m) => m.role !== 'client'))).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setDraft(emptyDraft()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setDraft({
      descripcion: it.descripcion || '',
      area: it.area || 'otro',
      responsable_id: it.responsable_id || 'none',
      fecha: it.fecha || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!draft.descripcion.trim()) { toast.error('Escribe la descripción de la acción'); return; }
    setSaving(true);
    try {
      const payload = {
        descripcion: draft.descripcion.trim(),
        area: draft.area,
        responsable_id: draft.responsable_id === 'none' ? null : draft.responsable_id,
        fecha: draft.fecha || null,
      };
      if (editing) {
        await serviceDeliveryAPI.updateWeeklyAction(clientId, editing.id, payload);
        toast.success('Acción actualizada');
      } else {
        await serviceDeliveryAPI.createWeeklyAction(clientId, payload);
        toast.success(payload.responsable_id ? 'Acción creada y tarea asignada' : 'Acción creada');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    }
    setSaving(false);
  };

  const setEstado = async (it, estado) => {
    setBusyId(it.id);
    try {
      await serviceDeliveryAPI.updateWeeklyAction(clientId, it.id, { estado });
      toast.success(estado === 'completada' ? 'Acción completada y archivada' : 'Acción reactivada');
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al actualizar');
    }
    setBusyId(null);
  };

  const handleDelete = async (it) => {
    if (!window.confirm('¿Eliminar esta acción? También se borrará la tarea vinculada.')) return;
    setBusyId(it.id);
    try {
      await serviceDeliveryAPI.deleteWeeklyAction(clientId, it.id);
      toast.success('Acción eliminada');
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al eliminar');
    }
    setBusyId(null);
  };

  const isArchive = view === 'archivo';

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openNewSub = (actionId) => {
    setSubActionId(actionId);
    setEditingSub(null);
    setSubDraft({ descripcion: '', responsable_id: 'none' });
    setSubDialogOpen(true);
  };
  const openEditSub = (actionId, sub) => {
    setSubActionId(actionId);
    setEditingSub(sub);
    setSubDraft({ descripcion: sub.descripcion || '', responsable_id: sub.responsable_id || 'none' });
    setSubDialogOpen(true);
  };

  const handleSaveSub = async () => {
    if (!subDraft.descripcion.trim()) { toast.error('Escribe la descripción de la subtarea'); return; }
    setSubSaving(true);
    try {
      const payload = {
        descripcion: subDraft.descripcion.trim(),
        responsable_id: subDraft.responsable_id === 'none' ? null : subDraft.responsable_id,
      };
      if (editingSub) {
        await serviceDeliveryAPI.updateSubtask(clientId, subActionId, editingSub.id, payload);
        toast.success('Subtarea actualizada');
      } else {
        await serviceDeliveryAPI.createSubtask(clientId, subActionId, payload);
        toast.success(payload.responsable_id ? 'Subtarea creada y tarea asignada' : 'Subtarea creada');
      }
      setSubDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar la subtarea');
    }
    setSubSaving(false);
  };

  const toggleSubEstado = async (actionId, sub) => {
    setBusyId(sub.id);
    try {
      const estado = sub.estado === 'completada' ? 'pendiente' : 'completada';
      await serviceDeliveryAPI.updateSubtask(clientId, actionId, sub.id, { estado });
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al actualizar la subtarea');
    }
    setBusyId(null);
  };

  const handleDeleteSub = async (actionId, sub) => {
    if (!window.confirm('¿Eliminar esta subtarea? También se borrará la tarea vinculada.')) return;
    setBusyId(sub.id);
    try {
      await serviceDeliveryAPI.deleteSubtask(clientId, actionId, sub.id);
      toast.success('Subtarea eliminada');
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al eliminar');
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-6" data-testid="weekly-actions-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" />
            Acciones Semanales
          </h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Checklist operativo de la semana de {clientName || 'este cliente'}. Al completar una acción se archiva
            (queda en el histórico). Asignar un responsable crea una tarea en su "Mis Tareas".
          </p>
        </div>
        {!isArchive && (
          <Button onClick={openNew} data-testid="add-weekly-action-btn" className="shrink-0">
            <Plus className="w-4 h-4 mr-2" /> Nueva acción
          </Button>
        )}
      </div>

      {/* Toggle activas / archivo */}
      <div className="inline-flex rounded-lg border p-1 bg-muted/40">
        <button
          onClick={() => setView('activas')}
          data-testid="view-activas-btn"
          className={`px-4 py-1.5 rounded-md text-sm transition-colors ${!isArchive ? 'bg-white shadow-sm font-medium' : 'text-muted-foreground'}`}
        >
          Activas
        </button>
        <button
          onClick={() => setView('archivo')}
          data-testid="view-archivo-btn"
          className={`px-4 py-1.5 rounded-md text-sm transition-colors inline-flex items-center gap-1.5 ${isArchive ? 'bg-white shadow-sm font-medium' : 'text-muted-foreground'}`}
        >
          <Archive className="w-3.5 h-3.5" /> Archivo
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {isArchive ? <Archive className="w-10 h-10 mx-auto mb-3 opacity-40" /> : <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-40" />}
            <p className="text-sm">{isArchive ? 'No hay acciones archivadas todavía.' : 'No hay acciones activas esta semana.'}</p>
            {!isArchive && <p className="text-xs">Haz clic en "Nueva acción" para añadir una tarea de la semana.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="weekly-actions-list">
          {items.map((it) => (
            <Card key={it.id} data-testid={`weekly-action-${it.id}`} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-3">
                <div className="flex items-start gap-3">
                  {!isArchive && isCEO && (
                    <Checkbox
                      checked={false}
                      disabled={busyId === it.id}
                      onCheckedChange={() => setEstado(it, 'completada')}
                      className="mt-1"
                      title="Completar y archivar la acción (solo CEO)"
                      data-testid={`complete-action-${it.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${isArchive ? 'line-through text-muted-foreground' : 'font-medium'}`}>{it.descripcion}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-1.5">
                      <Badge className={areaColor[it.area]} variant="secondary">{areaLabel(it.area)}</Badge>
                      {it.responsable_name && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="w-3 h-3" /> {it.responsable_name}
                        </span>
                      )}
                      {it.fecha && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" /> {fmtDate(it.fecha)}
                        </span>
                      )}
                      {it.assigned_by_name && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`assigned-by-${it.id}`}>
                          <UserPlus className="w-3 h-3" /> Asignada por {it.assigned_by_name}
                        </span>
                      )}
                      {(it.subtasks?.length > 0) && (
                        <Badge variant="outline" className="text-xs gap-1" data-testid={`subtask-progress-${it.id}`}>
                          <CheckSquare className="w-3 h-3" />
                          {it.subtasks.filter((s) => s.estado === 'completada').length} de {it.subtasks.length} completadas
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isArchive && (
                      <Button variant="ghost" size="icon" onClick={() => { if (!expanded[it.id]) toggleExpand(it.id); openNewSub(it.id); }} title="Añadir subtarea" data-testid={`add-subtask-btn-${it.id}`}>
                        <CornerDownRight className="w-4 h-4" />
                      </Button>
                    )}
                    {(it.subtasks?.length > 0) && (
                      <Button variant="ghost" size="icon" onClick={() => toggleExpand(it.id)} title="Ver subtareas" data-testid={`toggle-subtasks-${it.id}`}>
                        {expanded[it.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                    )}
                    {isArchive ? (
                      isCEO && (
                        <Button variant="ghost" size="icon" onClick={() => setEstado(it, 'pendiente')} disabled={busyId === it.id} title="Reactivar (solo CEO)" data-testid={`reopen-action-${it.id}`}>
                          {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                        </Button>
                      )
                    ) : (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-action-${it.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(it)} disabled={busyId === it.id} data-testid={`delete-action-${it.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Subtareas */}
                {(expanded[it.id] && (it.subtasks?.length > 0 || !isArchive)) && (
                  <div className="mt-3 ml-7 pl-3 border-l-2 border-muted space-y-1.5" data-testid={`subtasks-${it.id}`}>
                    {(it.subtasks || []).map((sub) => (
                      <div key={sub.id} className="flex items-start gap-2 group" data-testid={`subtask-${sub.id}`}>
                        <Checkbox
                          checked={sub.estado === 'completada'}
                          disabled={busyId === sub.id}
                          onCheckedChange={() => toggleSubEstado(it.id, sub)}
                          className="mt-0.5"
                          data-testid={`complete-subtask-${sub.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${sub.estado === 'completada' ? 'line-through text-muted-foreground' : ''}`}>{sub.descripcion}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {sub.responsable_name && (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <User className="w-3 h-3" /> {sub.responsable_name}
                              </span>
                            )}
                            {sub.assigned_by_name && (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <UserPlus className="w-3 h-3" /> Asignada por {sub.assigned_by_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {!isArchive && (
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditSub(it.id, sub)} data-testid={`edit-subtask-${sub.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteSub(it.id, sub)} disabled={busyId === sub.id} data-testid={`delete-subtask-${sub.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {!isArchive && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => openNewSub(it.id)} data-testid={`add-subtask-inline-${it.id}`}>
                        <Plus className="w-3 h-3 mr-1" /> Añadir subtarea
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="weekly-action-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar acción' : 'Nueva acción semanal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Descripción de la acción</Label>
              <Input
                value={draft.descripcion}
                onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
                placeholder="Ej: Revisar rendimiento de campañas de Ads y preparar reunión"
                data-testid="action-descripcion"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Área</Label>
                <Select value={draft.area} onValueChange={(v) => setDraft({ ...draft, area: v })}>
                  <SelectTrigger data-testid="action-area"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AREA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Día / fecha</Label>
                <Input type="date" value={draft.fecha} onChange={(e) => setDraft({ ...draft, fecha: e.target.value })} data-testid="action-fecha" />
              </div>
            </div>
            <div>
              <Label>Responsable <span className="text-muted-foreground text-xs">(se le crea una tarea)</span></Label>
              <Select value={draft.responsable_id} onValueChange={(v) => setDraft({ ...draft, responsable_id: v })}>
                <SelectTrigger data-testid="action-responsable"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-action-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Guardar cambios' : 'Crear acción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog crear/editar subtarea */}
      <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
        <DialogContent className="max-w-md" data-testid="subtask-dialog">
          <DialogHeader>
            <DialogTitle>{editingSub ? 'Editar subtarea' : 'Nueva subtarea'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Descripción de la subtarea</Label>
              <Input
                value={subDraft.descripcion}
                onChange={(e) => setSubDraft({ ...subDraft, descripcion: e.target.value })}
                placeholder="Ej: Crear creatividades"
                data-testid="subtask-descripcion"
              />
            </div>
            <div>
              <Label>Responsable <span className="text-muted-foreground text-xs">(se le crea una tarea)</span></Label>
              <Select value={subDraft.responsable_id} onValueChange={(v) => setSubDraft({ ...subDraft, responsable_id: v })}>
                <SelectTrigger data-testid="subtask-responsable"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveSub} disabled={subSaving} data-testid="save-subtask-btn">
              {subSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingSub ? 'Guardar cambios' : 'Crear subtarea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
